import React, { useState, useEffect } from 'react';
import { parseUnits, ethers } from "ethers";
import { getAddresses } from '../utils/address';
import { AroundUIHelperContract } from '../contracts/AroundUIHelper';
import { AroundRouterContract } from '../contracts/AroundRouter';
import { useEthers } from '../context/EthersContext';

const USDC_ABI = [
    "function approve(address spender, uint256 amount) public returns (bool)",
    "function allowance(address owner, address spender) view returns (uint256)",
    "function balanceOf(address owner) view returns (uint256)"
];


const MarketCreate = ({ onCreated, signerOverride }) => {
    const { signer: ctxSigner, provider: ctxProvider, addresses: ctxAddrs } = useEthers();
    const [quest, setQuest] = useState('');
    const [period, setPeriod] = useState('');
    const [expectVirtualLiquidity, setExpectVirtualLiquidity] = useState('');
    const [marketType, setMarketType] = useState('0');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [tokenDecimals, setTokenDecimals] = useState(6);
    const [guardedAmount, setGuardedAmount] = useState('');
    const [feeLoading, setFeeLoading] = useState(false);

    useEffect(() => {
        const addresses = ctxAddrs || {};
        const uiHelperAddress = addresses.AroundUIHelper;
        const usdcAddress = addresses.USDC;
        const provider = ctxProvider;
        async function initDecimals() {
            try {
                if (uiHelperAddress && usdcAddress && provider) {
                    const ui = new AroundUIHelperContract(uiHelperAddress, provider);
                    const d = await ui.getDecimals(usdcAddress);
                    const n = Number(d);
                    setTokenDecimals(Number.isFinite(n) ? n : 6);
                }
            } catch (_) {
                setTokenDecimals(6);
            }
        }
        initDecimals();
    }, [ctxAddrs, ctxProvider]);

    useEffect(() => {
        let cancelled = false;
        async function calcFee() {
            try {
                const addresses = ctxAddrs || {};
                const uiHelperAddress = addresses.AroundUIHelper;
                const provider = ctxProvider;
                const evlNum = Number(expectVirtualLiquidity);
                if (!evlNum || evlNum < 100000 || evlNum > 100000000) {
                    setGuardedAmount('');
                    return;
                }
                setFeeLoading(true);
                let amountBn = 0n;
                try {
                    if (uiHelperAddress && provider) {
                        const ui = new AroundUIHelperContract(uiHelperAddress, provider);
                        const evlBn = ethers.toBigInt(expectVirtualLiquidity);
                        const guarded = await ui.getGuardedAmount(evlBn, tokenDecimals);
                        amountBn = ethers.toBigInt(guarded || 0);
                    }
                } catch (_) { }
                if (amountBn === 0n) {
                    amountBn = parseUnits(String(expectVirtualLiquidity), tokenDecimals);
                }
                const formatted = ethers.formatUnits(amountBn, tokenDecimals);
                if (!cancelled) setGuardedAmount(formatted);
            } catch (_) {
                if (!cancelled) setGuardedAmount('');
            } finally {
                if (!cancelled) setFeeLoading(false);
            }
        }
        calcFee();
        return () => { cancelled = true; };
    }, [expectVirtualLiquidity, tokenDecimals, ctxAddrs, ctxProvider]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');
        try {
            const addresses = ctxAddrs || {};
            const routerAddress = addresses.AroundRouter;
            const usdcAddress = addresses.USDC;
            const uiHelperAddress = addresses.AroundUIHelper;
            const expectedVirtualLiquidityNum = Number(expectVirtualLiquidity);
            if (!expectedVirtualLiquidityNum || expectedVirtualLiquidityNum < 100000 || expectedVirtualLiquidityNum > 100000000) {
                throw new Error('Expected virtual liquidity must be between 100,000 and 100,000,000');
            }
            const durationSeconds = Number(period);
            if (!durationSeconds || durationSeconds < 100000) {
                throw new Error('Duration must be at least 100000 seconds');
            }
            const signer = signerOverride || ctxSigner;
            if (!signer) throw new Error('Wallet not connected.');
            if (!routerAddress) throw new Error('Router address not configured.');
            if (!usdcAddress) throw new Error('USDC address not configured.');

            const net = await ctxProvider.getNetwork().catch(() => null);
            const pChain = Number(net?.chainId);
            const cfgChainId = Number(addresses?.chainId);
            if (!Number.isFinite(pChain) || !Number.isFinite(cfgChainId) || pChain !== cfgChainId) throw new Error('Network mismatch');
            const usdcCode = await ctxProvider.getCode(usdcAddress).catch(() => '0x');
            const routerCode = await ctxProvider.getCode(routerAddress).catch(() => '0x');
            if (!usdcCode || usdcCode === '0x') throw new Error('USDC not deployed on current network');
            if (!routerCode || routerCode === '0x') throw new Error('Router not deployed on current network');
            const usdcContract = new ethers.Contract(usdcAddress, USDC_ABI, signer);
            const provider = ctxProvider;
            const ui = uiHelperAddress && provider ? new AroundUIHelperContract(uiHelperAddress, provider) : null;
            let tokenDecimals = 6;
            try {
                if (ui) {
                    const d = await ui.getDecimals(usdcAddress);
                    tokenDecimals = Number(d) || 6;
                }
            } catch (e) { console.error('getDecimals failed', e); }

            let evlBn = ethers.toBigInt(expectVirtualLiquidity);
            let approveAmount = 0n;
            try {
                if (ui) {
                    const guarded = await ui.getGuardedAmount(evlBn, tokenDecimals);
                    approveAmount = ethers.toBigInt(guarded || 0);
                }
            } catch (e) { console.error('getGuardedAmount failed', e); }
            if (approveAmount === 0n) {
                approveAmount = parseUnits(String(expectVirtualLiquidity), tokenDecimals);
            }
            const owner = await signer.getAddress();
            const bal = await usdcContract.balanceOf(owner);
            const balBn = ethers.toBigInt(bal || 0);
            if (balBn < approveAmount) {
                throw new Error(`Insufficient USDC balance: need ${ethers.formatUnits(approveAmount, tokenDecimals)}`);
            }
            let needApprove = true;
            let currentBn = 0n;
            try {
                const current = await usdcContract.allowance(owner, routerAddress);
                currentBn = ethers.toBigInt(current || 0);
                needApprove = (currentBn < approveAmount);
            } catch (e) { console.error('allowance read failed', e); }
            if (needApprove) {
                if (currentBn > 0n) {
                    const resetTx = await usdcContract.approve(routerAddress, 0);
                    await resetTx.wait();
                }
                const approveTx2 = await usdcContract.approve(routerAddress, approveAmount);
                await approveTx2.wait();
            }

            // 3. Create Market via Router (single transaction)
            const router = new AroundRouterContract(routerAddress, signer);
            const params = {
                marketType: Number(marketType),
                period: ethers.toBigInt(Number(period || 0)),
                expectVirtualLiquidity: ethers.toBigInt(expectVirtualLiquidity),
                collateral: usdcAddress,
                creator: owner,
                quest: quest
            };
            console.log('%c🚀[params]-90:', 'color: #8321e0', params);

            const createTx = await router.createMarket(params, { gasLimit: 5_000_000 });
            const createReceipt = await createTx.wait();
            if (createReceipt.status === 1) {
                setSuccess('Market created successfully');
            } else {
                throw new Error('Create market transaction failed');
            }
            setQuest('');
            setPeriod('');
            setExpectVirtualLiquidity('');
            if (onCreated) onCreated();
        } catch (err) {
            console.log('%c🚀[err]-64:', 'color: #7f0f43', err);
            const decoded = (err && (err.reason || err.shortMessage)) || '';
            const named = err && err.info && err.info.errorName ? `${err.info.errorName}` : '';
            const msg = decoded || named || (err && err.message) || 'Creation failed';
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="market-create-container">
            <div className="back-nav"><a className="back-link" href="/">← Back to Markets</a></div>
            <div className="market-create-card">
                <div className="create-card-header">
                    <h2>Create New Market</h2>
                    <p className="subtitle">Launch a new prediction market for the community.</p>
                </div>

                <div className="create-card-body">
                    <form onSubmit={handleSubmit}>
                        <div className="form-section">
                            <div className="input-group">
                                <label>Market Category</label>
                                <div className="select-wrapper">
                                    <select value={marketType} onChange={e => setMarketType(e.target.value)} required>
                                        <option value="0">Other</option>
                                        <option value="1">Crypto</option>
                                        <option value="2">Sport</option>
                                        <option value="3">Game</option>
                                        <option value="4">Politics</option>
                                        <option value="5">Economy</option>
                                        <option value="6">Tech</option>
                                        <option value="7">Weather</option>
                                    </select>
                                </div>
                            </div>

                            <div className="input-group">
                                <label>Question</label>
                                <textarea
                                    value={quest}
                                    onChange={e => setQuest(e.target.value)}
                                    required
                                    placeholder="e.g., Will Bitcoin exceed $100k by the end of 2024?"
                                    rows="3"
                                    maxLength={120}
                                />
                            </div>
                        </div>

                        <div className="input-group">
                            <label>Duration (Seconds)</label>
                            <input
                                type="number"
                                value={period}
                                onChange={e => setPeriod(e.target.value)}
                                required
                                placeholder="e.g. 100000"
                                min="100000"
                            />
                        </div>
                        <div className="input-group">
                            <div className="label-row">
                                <label>Expected Virtual Liquidity</label>
                                <span className="label-helper">(100,000–100,000,000)</span>
                            </div>
                            <input
                                type="number"
                                value={expectVirtualLiquidity}
                                onChange={e => setExpectVirtualLiquidity(e.target.value)}
                                required
                                placeholder="Enter expected virtual liquidity"
                                min="100000"
                                max="100000000"
                                step="1"
                            />
                            <div className="label-row" style={{ marginTop: 6 }}>
                                <span className="label-helper">Creation fee (USDC)</span>
                                <span className="label-helper">{feeLoading ? 'Calculating…' : (guardedAmount ? `${guardedAmount} USDC` : '-')}</span>
                            </div>
                        </div>

                        <div className="form-actions">
                            <button type="submit" disabled={loading} className="submit-btn">
                                {loading ? (
                                    <span className="loading-text">
                                        <span className="spinner-small"></span> Creating...
                                    </span>
                                ) : 'Create Market'}
                            </button>
                        </div>

                        {error && <div className="message error-msg">{error}</div>}
                        {success && <div className="message success-msg">{success}</div>}
                    </form>
                </div>
            </div>
        </div>
    );
};

export default MarketCreate;
